import {
    ALL_AXES,
    Analysis,
    DisplaySymbol,
    DisplayUnit,
    getDisplayUnitAndSymbol,
    IMeasurement,
    isFiniteNumber,
    Nil,
    Segmentation,
    simpleConvert,
    Unit,
    UserPreferredUnit,
} from '@core';
import { GraphLineNode } from '@data';
import { CorridorDataPoint } from '@ui';

import { CorridorDataPoints, createCorridorDataPoints } from './corridors';

type GraphExtras = {
    includeApexPoints: boolean;
    includeTransitionPoints: boolean;
};

type TransitionPoint = {
    frame: number;
    value: number;
};

function getMeasurementByName(analysis: Analysis | Nil, measurementName: string) {
    if (!analysis) {
        return null;
    }
    return analysis.data.analysis?.measurements?.find((m) => m.measurement_name === measurementName);
}

function findNearestTransitionToFrame(
    transitions: Record<string, TransitionPoint[]>,
    targetFrame: number,
): Record<string, TransitionPoint> {
    // Initialize result object
    const result: Record<string, TransitionPoint> = {};

    // Process each graph line
    for (const [key, points] of Object.entries(transitions)) {
        // Point frames can't be >targetFrame
        const validPoints = points.filter((point) => point.frame <= targetFrame);

        if (validPoints.length === 0) continue;

        // Find the point with the closest frame to targetFrame
        const closestPoint = validPoints.reduce((closest, current) => {
            const currentDiff = Math.abs(current.frame - targetFrame);
            const closestDiff = Math.abs(closest.frame - targetFrame);
            return currentDiff < closestDiff ? current : closest;
        }, points[0]);

        // Add to result
        result[key] = closestPoint;
    }
    return result;
}

type MeasurementValue = {
    current: number | Nil;
    comparison: number | Nil;
    displayUnit: DisplayUnit;
    displaySymbol: DisplaySymbol;
};

export type GraphDataFromLinesParams = {
    corridorsByGraphLine: Record<string, CorridorDataPoints>;
    graphLines?: GraphLineNode[];
    analysis: Analysis | Nil;
    comparisonAnalysis: Analysis | Nil;
    segmentation: {
        current: Segmentation | null;
        comparison: Segmentation | null;
    };
    extras: GraphExtras | Nil;
    userPreferredUnit: UserPreferredUnit;
    totalFrames?: number | Nil;
};

export type GraphDataFromLinesReturn = {
    dataPoints: Array<Record<string, MeasurementValue | Nil>>;
    isQuickAnalysis: boolean;
    apex: Record<string, { frame: number; value: number }>;
    transition: Record<string, TransitionPoint>;
    displaySymbol: DisplaySymbol;
    corridorDataPoints?: Record<string, CorridorDataPoint[]>;
} | null;

export function graphDataFromLines({
    totalFrames,
    graphLines,
    analysis,
    comparisonAnalysis,
    segmentation,
    extras,
    userPreferredUnit,
    corridorsByGraphLine,
}: GraphDataFromLinesParams): GraphDataFromLinesReturn {
    const { current: currentSegmentation, comparison: comparisonSegmentation } = segmentation;
    if (!analysis || !currentSegmentation || !graphLines) {
        return {
            dataPoints: [],
            isQuickAnalysis: false,
            apex: {},
            transition: {},
            displaySymbol: '%',
        };
    }

    const displaySymbol = getDisplayUnitAndSymbol(graphLines?.[0]?.timeSeries?.unit as Unit, userPreferredUnit).symbol;
    const dataLength = totalFrames ?? getDataLength(graphLines, analysis);

    if (!isFiniteNumber(dataLength)) {
        console.warn('[graphDataFromLines]: No valid measurements found');
        return {
            dataPoints: [],
            isQuickAnalysis: false,
            apex: {},
            transition: {},
            displaySymbol,
        };
    }

    const frameNumbers = Object.entries(currentSegmentation).map(([_, value]) => value);
    const isQuickAnalysis = dataLength <= 10;

    // Comparison diff is synced to P7
    const compDiff = currentSegmentation['P7'] - (comparisonSegmentation?.['P7'] ?? 0);

    // Track apex and transition points
    const apexTracking: Record<string, { frame: number; value: number }> = {};
    const transitionTracking: Record<string, { frame: number; value: number }[]> = {};
    // Construct data points with (potential) offset comparison data
    const dataPoints = Array.from({ length: dataLength }, (_, index) => {
        return graphLines.reduce(
            (dataPoint: Record<string, MeasurementValue | Nil> & { frame: number }, line) => {
                const axis = (line.axis || 'x') as (typeof ALL_AXES)[number];
                if (!line.timeSeries?.id) {
                    console.warn(`[graphDataFromLines]: Incomplete data for line ${line.id}:`, line);
                    dataPoint[line.id] = null;
                    return dataPoint;
                }

                const measurement = getMeasurementByName(analysis, line.timeSeries.id);
                const comparisonMeasurement = getMeasurementByName(comparisonAnalysis, line.timeSeries.id);
                const axisIndex = ALL_AXES.indexOf(axis);
                const effectiveFrame = isQuickAnalysis ? frameNumbers[index] : index;
                const { value, comparisonValue, prevValue } = getValueAndComparison({
                    measurement,
                    comparisonMeasurement,
                    axisIndex,
                    compDiff,
                    index,
                });
                const current = simpleConvert({
                    value,
                    userPreferredUnit,
                    unit: line.timeSeries.unit as Unit,
                    rounding: 0,
                });
                const { unit: displayUnit, symbol: displaySymbol } = getDisplayUnitAndSymbol(
                    line.timeSeries.unit as Unit,
                    userPreferredUnit,
                );
                dataPoint[line.id] = {
                    current,
                    comparison: simpleConvert({
                        value: comparisonValue,
                        userPreferredUnit,
                        unit: line.timeSeries.unit as Unit,
                        rounding: 0,
                    }),
                    displayUnit,
                    displaySymbol,
                };

                // Track apex points (the peak of `value`)
                if (extras?.includeApexPoints && isFiniteNumber(current)) {
                    if (!apexTracking[line.id] || current > apexTracking[line.id].value) {
                        apexTracking[line.id] = {
                            frame: effectiveFrame,
                            value: current,
                        };
                    }
                }

                /**
                 * Transition point is when a value passes from negative to positive
                 * We collect all of these points but we only return the point that is
                 * closest to P7
                 */
                if (
                    extras?.includeTransitionPoints &&
                    index > 0 &&
                    isFiniteNumber(prevValue) &&
                    isFiniteNumber(value)
                ) {
                    if (prevValue < 0 && value > 0) {
                        transitionTracking[line.id] = transitionTracking[line.id] ?? [];
                        transitionTracking[line.id].push({
                            frame: effectiveFrame,
                            value,
                        });
                    }
                }

                return dataPoint;
            },
            { frame: isQuickAnalysis ? frameNumbers[index] : index } as Record<string, MeasurementValue | Nil> & {
                frame: number;
            },
        );
    });

    return {
        dataPoints,
        isQuickAnalysis,
        apex: apexTracking,
        transition: extras?.includeTransitionPoints
            ? findNearestTransitionToFrame(transitionTracking, currentSegmentation['P7'])
            : {},
        displaySymbol,
        corridorDataPoints: createCorridorDataPoints(graphLines, corridorsByGraphLine, currentSegmentation),
    };
}

type GetValueAndComparisonParams = {
    measurement: IMeasurement | null | undefined;
    comparisonMeasurement: IMeasurement | null | undefined;
    axisIndex: number;
    compDiff: number;
    index: number;
};

function getValueAndComparison({
    measurement,
    comparisonMeasurement,
    compDiff,
    axisIndex,
    index,
}: GetValueAndComparisonParams) {
    const comparisonIndex = index - compDiff;
    return {
        value: measurement?.value?.[axisIndex]?.[index] ?? null,
        prevValue: measurement?.value[axisIndex][index - 1] ?? null,
        comparisonValue: comparisonMeasurement?.value?.[axisIndex]?.[comparisonIndex] ?? null,
    };
}

const getDataLength = (lines: GraphLineNode[], analysis: Analysis) =>
    lines?.reduce((length: number | null, line) => {
        if (!line.timeSeries?.id) {
            return length;
        }
        const axis = line?.axis || 'x';
        const measurement = getMeasurementByName(analysis, line.timeSeries.id);
        const axisIndex = ALL_AXES.indexOf(axis as (typeof ALL_AXES)[number]);
        const currentLength = measurement?.value[axisIndex].length;
        if (axisIndex === -1 || !currentLength) {
            return length;
        }
        if (length === null) {
            return currentLength;
        }
        if (currentLength !== length) {
            throw new Error(
                `[graphDataFromLines]: Inconsistent measurement lengths. Expected ${length}, but found ${currentLength} for line ${line.id}`,
            );
        }
        return length;
    }, null);
